import { useEffect, useState } from "react";
import { Box, Button, Chip, TextField } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../../design/brainTheme";
import { PersonAvatar, SectionCard, EmptyState, Icon } from "../../../mui";
import { useProfile } from "../../../lib/profile";
import {
  getPersonApi,
  updateMyProfile,
  uploadMyAvatar,
  type PresenceSession,
} from "../../../lib/brain-api";
import { RightNowPanel } from "./RightNowPanel";
import { PetCard, PetConfigurator } from "./PetCard";
import { useAgent } from "../../../agent/AgentProvider";

const c = brainTokens.color;

export function PersonPage() {
  const { slug = "" } = useParams();
  const { profile } = useProfile();
  const { setPageContext } = useAgent();
  const { enqueueSnackbar } = useSnackbar();
  const [person, setPerson] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState("");
  const [petOpen, setPetOpen] = useState(false);

  const reload = async () => {
    const p = await getPersonApi(slug);
    setPerson(p);
    setBio(String(p.bio ?? ""));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch((e) => enqueueSnackbar(e instanceof Error ? e.message : "Failed to load", { variant: "error" }))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Person entity for the agent dock — the URL floor doesn't cover /people/:slug.
  useEffect(() => {
    setPageContext({
      route: `/people/${slug}`,
      entity: { type: "person", id: slug, name: person?.displayName },
    });
    return () => setPageContext(null);
  }, [setPageContext, slug, person?.displayName]);

  const isSelf =
    !!profile &&
    !!person &&
    (profile.userId === person.userId ||
      profile.email?.split("@")[0]?.toLowerCase() === String(person.slug).toLowerCase());

  const saveBio = async () => {
    try {
      await updateMyProfile({ bio });
      enqueueSnackbar("Saved", { variant: "success" });
      setEditing(false);
      await reload();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Save failed", { variant: "error" });
    }
  };

  const onAvatar = async (file: File) => {
    const buf = await file.arrayBuffer();
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
    await uploadMyAvatar(b64, file.type || "image/png");
    await reload();
  };

  const acceptExpertise = async (tag: string, accept: boolean) => {
    const expertise = person.expertise ?? { accepted: [], dismissed: [], suggested: [] };
    const accepted = new Set<string>(expertise.accepted ?? []);
    const dismissed = new Set<string>(expertise.dismissed ?? []);
    if (accept) {
      accepted.add(tag);
      dismissed.delete(tag);
    } else {
      dismissed.add(tag);
      accepted.delete(tag);
    }
    await updateMyProfile({
      expertise: { accepted: [...accepted], dismissed: [...dismissed] },
    });
    await reload();
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 960, p: "24px 32px 24px 0" }}>Loading…</Box>
    );
  }
  if (!person) {
    return (
      <Box sx={{ maxWidth: 960, p: "24px 32px 24px 0" }}>
        <EmptyState icon="users" title="Person not found" />
      </Box>
    );
  }

  const sessions = (person.presence ?? []) as PresenceSession[];

  return (
    <Box sx={{ maxWidth: 960, p: "24px 32px 56px 0" }}>
        <ButtonLink to="/aeronet?tab=who" />
        <Box
          sx={{
            background: `linear-gradient(135deg, ${c.tealTint2}, ${c.panelBg2})`,
            border: `1px solid ${c.border}`,
            borderRadius: `${brainTokens.radius.panel}px`,
            p: 3,
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <PersonAvatar name={person.displayName} avatarUrl={person.avatarUrl} size={72} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontSize: 26, fontWeight: 700, color: c.ink }}>{person.displayName}</Box>
              <Box sx={{ fontSize: 13, color: c.muted2, mb: 1 }}>{person.role}</Box>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
                {(person.departments ?? []).map((d: string) => (
                  <Chip key={d} size="small" label={d} />
                ))}
              </Box>
              {editing ? (
                <Box>
                  <TextField fullWidth multiline minRows={3} value={bio} onChange={(e) => setBio(e.target.value)} />
                  <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                    <Button size="small" variant="contained" onClick={() => void saveBio()}>
                      Save
                    </Button>
                    <Button size="small" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ fontSize: 14.5, color: c.ink3, lineHeight: 1.55 }}>
                  {person.bio || "No bio yet — a one-liner goes a long way."}
                </Box>
              )}
            </Box>
            {isSelf && !editing && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
                  Edit my page
                </Button>
                <Button size="small" component="label" variant="text">
                  Upload photo
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onAvatar(f).catch((err) => enqueueSnackbar(String(err), { variant: "error" }));
                    }}
                  />
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" }, gap: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, minWidth: 0 }}>
            <RightNowPanel sessions={sessions} />

            <SectionCard label="Availability">
              {person.availability ? (
                <Box sx={{ fontSize: 13.5, color: c.ink3 }}>
                  {(person.availability.days ?? []).join(", ") || "—"} · {person.availability.hours || "—"}
                  {person.availability.partTime ? " · part-time" : ""}
                  {person.availability.awayUntil
                    ? ` · away until ${person.availability.awayUntil}${person.availability.fallback ? ` — ask ${person.availability.fallback}` : ""}`
                    : ""}
                </Box>
              ) : (
                <Box sx={{ fontSize: 13, color: c.muted2 }}>No availability set yet.</Box>
              )}
            </SectionCard>

            <SectionCard label="Expertise">
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {(person.expertise?.accepted ?? []).map((t: string) => (
                  <Chip key={t} label={t} size="small" color="primary" variant="outlined" />
                ))}
                {(person.expertise?.suggested ?? []).map((t: string) => (
                  <Chip
                    key={`s-${t}`}
                    label={`${t} · suggested`}
                    size="small"
                    variant="outlined"
                    sx={{ borderStyle: "dashed" }}
                    onDelete={isSelf ? () => void acceptExpertise(t, false) : undefined}
                    onClick={isSelf ? () => void acceptExpertise(t, true) : undefined}
                  />
                ))}
                {(person.expertise?.accepted ?? []).length === 0 &&
                  (person.expertise?.suggested ?? []).length === 0 && (
                    <Box sx={{ fontSize: 13, color: c.muted2 }}>No expertise tags yet.</Box>
                  )}
              </Box>
            </SectionCard>

            <SectionCard label="Current projects">
              {(person.projects ?? []).length === 0 ? (
                <Box sx={{ fontSize: 13, color: c.muted2 }}>No active projects as owner/deputy.</Box>
              ) : (
                (person.projects as any[]).map((p) => (
                  <Box
                    key={p.projectId}
                    component={RouterLink}
                    to={`/projects/${p.projectId}`}
                    sx={{ display: "block", py: 0.75, color: c.tealInk, textDecoration: "none", fontSize: 14 }}
                  >
                    {p.name} · {p.role}
                  </Box>
                ))
              )}
            </SectionCard>

            <SectionCard label="Recent activity">
              <Box sx={{ fontSize: 13, color: c.muted2 }}>
                {(person.recentActivity ?? []).length === 0
                  ? "Quiet lately — nothing wrong with that."
                  : "Activity feed wiring lands with denser browse filters."}
              </Box>
            </SectionCard>

            <SectionCard label="Decisions I made">
              <Box sx={{ fontSize: 13, color: c.muted2 }}>
                {(person.decisions ?? []).length === 0
                  ? "No decisions captured yet."
                  : "Decision list from type=decision thoughts."}
              </Box>
            </SectionCard>
          </Box>

          <Box>
            <PetCard
              pet={person.pet}
              stats={person.petStats}
              canConfigure={isSelf}
              onConfigure={() => setPetOpen(true)}
            />
          </Box>
        </Box>

        <PetConfigurator
          open={petOpen}
          initial={person.pet}
          onClose={() => setPetOpen(false)}
          onSave={async (pet) => {
            await updateMyProfile({ pet });
            setPetOpen(false);
            await reload();
          }}
        />
      </Box>
  );
}

function ButtonLink({ to }: { to: string }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        color: c.muted2,
        textDecoration: "none",
        fontSize: 13,
        mb: 2,
        "&:hover": { color: c.tealInk },
      }}
    >
      <Icon name="arrow-left" size={14} /> Back to Who are we
    </Box>
  );
}
